import {
  Alert,
  Box,
  Button,
  Container,
  Flex,
  LinkButton,
  Text,
} from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEnvironmentValues } from "../../hooks/environment";
import { useTokensValues } from "../../hooks/tokens";
import { useProductInfoDrawer } from "../../hooks/useProductInfoDrawer";
import type { WithMetadata } from "../../pages";
import type { RecommendationsProps } from "../../pages";
import { ProductInfoDrawer } from "../shared/ProductInfoDrawer";
import { Disclaimers } from "../Showcase/Disclaimers";
import { useShowcaseReveal } from "../Showcase/useShowcaseReveal";
import { RecommendationCard } from "./RecommendationCard/RecommendationCard";
import {
  getDisabledProductsWithMessages,
  type ProductName,
} from "./utils/productExclusivity";
import "./Recommendations.css";

const DEFAULT_TITLE =
  "Here are the coverage options we suggest for your business";

export const Recommendations = ({
  recommendations,
  disclaimers,
  title = DEFAULT_TITLE,
  pageName,
  pageIndex,
  sectionName,
  offerUuid,
  actions,
  error,
}: WithMetadata<RecommendationsProps>) => {
  useShowcaseReveal();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [activeProductId, setActiveProductId] = useState<string | null>(null);
  const { isOpen, activeProduct, openDrawer, closeDrawer } =
    useProductInfoDrawer();
  const [validationError, setValidationError] = useState<string | null>(null);
  const validationErrorRef = useRef<HTMLDivElement>(null);

  const { snowplow } = useSnowplowContext();
  const { site, journeyId, vertical } = useEnvironmentValues();
  const { authenticityToken, journeyToken } = useTokensValues();

  useEffect(() => {
    if (validationError && validationErrorRef.current) {
      validationErrorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [validationError]);

  const disabledProductsMap = useMemo(() => {
    const selectedProducts = Array.from(selectedIds) as ProductName[];
    return getDisabledProductsWithMessages(selectedProducts);
  }, [selectedIds]);

  useEffect(() => {
    setSelectedIds(prevSelectedIds => {
      if (!recommendations.length) {
        return new Set();
      }

      const validIds = new Set(recommendations.map(rec => rec.id));
      const filteredIds = new Set(
        [...prevSelectedIds].filter(id => validIds.has(id)),
      );

      return filteredIds;
    });
  }, [recommendations]);

  const triggerAnalyticsEvent = useCallback(
    (_id: string, fieldName: string, checked: boolean) => {
      const question = fieldName;
      const answerKey = `${question}_yes`;
      const answer = checked
        ? [JSON.stringify({ [answerKey]: "yes" })]
        : ["{}"];

      snowplow?.trigger("checkboxFormQuestionAnswered", {
        context: { site, journeyId, vertical },
        pageIndex,
        pageName,
        sectionName,
        question,
        answer,
      });

      snowplow?.trigger(
        checked
          ? "recommendationsCoverageAdded"
          : "recommendationsCoverageRemoved",
        { fieldName: question },
      );
    },
    [snowplow, site, journeyId, vertical, pageIndex, pageName, sectionName],
  );

  const selectCard = useCallback(
    (id: string) => {
      setValidationError(null);

      const fieldName =
        recommendations.find(r => r.id === id)?.fieldName ?? `${id}_coverage`;

      setSelectedIds(prevSelectedIds => {
        const newSelectedIds = new Set(prevSelectedIds);
        const willBeSelected = !newSelectedIds.has(id);

        if (willBeSelected) {
          newSelectedIds.add(id);
        } else {
          newSelectedIds.delete(id);
        }

        triggerAnalyticsEvent(id, fieldName, willBeSelected);

        return newSelectedIds;
      });
    },
    [triggerAnalyticsEvent, recommendations],
  );

  const handleOpenDrawer = useCallback(
    (productId: string, drawerTitle: string, content: string) => {
      setActiveProductId(productId);
      openDrawer({ title: drawerTitle, content });
      snowplow?.trigger("learnMoreModalOpened", { product: productId });
    },
    [openDrawer, snowplow],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      if (selectedIds.size === 0) {
        event.preventDefault();
        setValidationError("Please select at least one coverage");
        validationErrorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    },
    [selectedIds],
  );

  const backAction = actions?.find(action => action.actionType === "back");
  const submitAction = actions?.find(action => action.actionType === "submit");
  const shouldShowBackendAlert = Boolean(error?.message);

  return (
    <Container
      size="sm"
      data-testid="recommendations-container"
      data-recommendations-hydrated={true}
    >
      <form
        {...(submitAction?.url && { action: submitAction.url })}
        method="POST"
        className="mobius-recommendations__form"
        onSubmit={handleSubmit}
        data-testid="recommendations-form"
      >
        <input
          type="hidden"
          name="authenticity_token"
          value={authenticityToken ?? ""}
        />
        <input type="hidden" name="journey_token" value={journeyToken ?? ""} />
        {offerUuid && (
          <input type="hidden" name="offer_uuid" value={offerUuid} />
        )}

        <Flex gap="md" className="mobius-recommendations__layout">
          <Box className="mobius-recommendations">
            <Flex
              className="mobius-recommendations__intro"
              flexDirection="column"
              gap="xs"
            >
              <Text
                id="recommendations-title"
                variant="title"
                elementType="h2"
                className="mobius-recommendations__title"
              >
                {title}
              </Text>
            </Flex>

            {shouldShowBackendAlert && (
              <Alert
                variant="error"
                className="mobius-recommendations__error"
                data-testid="recommendations-backend-alert"
              >
                {error?.message}
              </Alert>
            )}

            {validationError && (
              <Alert
                ref={validationErrorRef}
                variant="error"
                className="mobius-recommendations__error"
                data-testid="recommendations-validation-error"
              >
                {validationError}
              </Alert>
            )}

            <div
              className="mobius-recommendations__grid"
              role="group"
              aria-labelledby="recommendations-title"
            >
              {recommendations.map(recommendation => {
                const unavailableMessage = disabledProductsMap.get(
                  recommendation.id as ProductName,
                );

                return (
                  <RecommendationCard
                    key={recommendation.id}
                    recommendation={recommendation}
                    isSelected={selectedIds.has(recommendation.id)}
                    onSelect={selectCard}
                    onOpenDrawer={handleOpenDrawer}
                    unavailableMessage={unavailableMessage}
                  />
                );
              })}
            </div>
          </Box>
        </Flex>

        {(backAction || submitAction) && (
          <Flex
            className="mobius-actions"
            justifyContent={backAction ? "space-between" : "flex-end"}
            alignItems="center"
          >
            {backAction && (
              <LinkButton
                className="mobius-actions__back"
                variant="secondary"
                size="md"
                href={backAction.url}
              >
                {backAction.label}
              </LinkButton>
            )}
            {submitAction && (
              <Button type="submit" variant="primary" size="md">
                {submitAction.label}
              </Button>
            )}
          </Flex>
        )}
        {disclaimers && <Disclaimers disclaimers={disclaimers} />}
      </form>

      <ProductInfoDrawer
        isOpen={isOpen}
        onClose={closeDrawer}
        title={activeProduct?.title}
        content={activeProduct?.content}
        productId={activeProductId ?? undefined}
        isSelected={activeProductId ? selectedIds.has(activeProductId) : false}
        onToggle={selectCard}
        isUnavailable={
          activeProductId
            ? disabledProductsMap.has(activeProductId as ProductName)
            : false
        }
      />
    </Container>
  );
};
